import type { SortDirectionEnum, ViewModeEnum } from "@eu.mdotm.api/smart-workspaces";
import type { EnableAlertDto, FieldKey, InvestmentExportConverterType, MetricParameters } from "@eu.mdotm.api/sphere";
import env from "$root/env";
import type { IUser } from "$root/functional-areas/user";
import { iubendaPurposeId } from "./initIubenda";

let mixPanelConfig = {
	uid: null as null | string,
	impersonating: false,
	isInternal: false,
};

export function configMixPanel(config: Partial<typeof mixPanelConfig>): void {
	mixPanelConfig = { ...mixPanelConfig, ...config };
}

const mixPanelScriptId = "mixpanel-3cdb2b2f-8607-4ee9-b7c2-a405891f0916";

const initMixPanel = (token: string, debug: boolean, iubendaTag: string, bypassIubenda: boolean): void => {
	// Enabling the debug mode flag is useful during implementation,
	// but it's recommended you remove it for production
	const mixPanelScript = document.createElement("script");
	mixPanelScript.text = `(function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
	for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));a.push([e,call2])}}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d};b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?
	MIXPANEL_CUSTOM_LIB_URL:"file:"===f.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}})(document,window.mixpanel||[]);

	mixpanel.init('${token}', {
		debug: ${debug},
		api_host: "https://fletcher.mdotm.net",
	});`;

	mixPanelScript.id = mixPanelScriptId;

	if (!bypassIubenda) {
		mixPanelScript.type = "text/plain";
		mixPanelScript.setAttribute("class", "_iub_cs_activate-inline");
		mixPanelScript.setAttribute("data-iub-purposes", `${iubendaTag}`);
	} else {
		mixPanelScript.type = "application/javascript";
	}
	document.head.appendChild(mixPanelScript);
};

const platformUser = "platform@mdotm.eu";

export const initMixPanelCUser = (user: IUser): void => {
	if (user.email === platformUser && document.getElementById(mixPanelScriptId) == null) {
		initMixPanel(env.mixPanelKey, true, String(iubendaPurposeId), true);
	}

	if (typeof window.mixpanel === "undefined") {
		return;
	}
	if (
		mixPanelConfig.impersonating ||
		window.location.hostname === "localhost" ||
		(mixPanelConfig.isInternal && mixPanelConfig.uid !== platformUser)
	) {
		window.mixpanel.opt_out_tracking();
		return;
	}
	if (user.signedIn) {
		window.mixpanel.identify(user.email);
		window.mixpanel.people.set({
			$name: user.email,
			$email: user.email,
			$distinct_id: user.email,
			Uid: user.email,
		});

		window.mixpanel.opt_in_tracking();
	}
};

const intentIdConfig = {
	prefix: "intent-" + Math.random().toString(36).slice(2) + Date.now().toString(36),
	latest: 0,
};

export function generateIntentId(): string {
	intentIdConfig.latest++;
	return intentIdConfig.prefix + "-" + intentIdConfig.latest;
}

export type MixPanelEventMap = {
	pageView: { pathname: string };
	"universe.entityActions.createIntent": { intentId: string };
	"universe.entityActions.create": {
		intentId?: string;
		universe: { uuid: string; name: string; type: "WITH_GROUPS" | "WITHOUT_GROUPS" };
	};
	"reference.entityActions.createIntent": { intentId: string };
	"reference.entityActions.create": { intentId?: string; reference: { uuid: string; name: string } };
	"marketView.entityActions.createIntent": { intentId: string };
	"marketView.entityActions.create": { intentId?: string; marketView: { uuid: string; name: string } };
	"instrument.entityAction.generateCommentary": { ticker: string };
	// User clicked "Apply now", flushing the server-side propagation debounce instead of letting it
	// fire. `scheduledAt` is the deadline they skipped — captured before the flush, since it is null
	// afterwards — and is null when data was dirty with no run yet scheduled. Compare it against the
	// event time to judge whether the debounce window is well-sized, and count these against total edit
	// sessions to see whether the escape hatch earns its keep.
	"instrument.entityAction.propagateNow": { scheduledAt: string | null };
	"reportBuilder.entityActions.create": { report: { uuid: string; name: string } };

	// User clicked "Update Charts" in the portfolio lab. `updatedColumns` is how many columns were
	// out-of-sync and thus recomputed; `columns` is the total loaded at the time.
	"portfolioLab.updateCharts": { updatedColumns: number; columns: number };
	// A portfolio-lab session was reshaped (column added/removed, composition edited, …). `trigger`
	// discriminates the interaction; the rest is a snapshot of the session right after the change.
	"portfolioLab.session.change": {
		trigger:
			| "addColumn"
			| "removeColumn"
			| "removeAllColumns"
			| "addPortfolioToColumn"
			| "addInstrumentToColumn"
			| "resetColumn";
		columns: number;
		investmentsByType: { INVESTMENT: number; INVESTMENT_PROPOSAL: number; BENCHMARK: number; REFERENCE: number };
		// The loaded columns, identified for diagnostics: `virtualUuid` is the lab copy, `uuid` the
		// source (real) investment.
		investments: { virtualUuid: string; uuid: string; type: string }[];
		visibleCharts: number;
	};

	// A workspace was created: `new` is the "New workspace" action (the platform seeds the new workspace
	// from an existing one), `duplicate` the explicit duplication; `source` is the copied workspace.
	"workspace.entityActions.create": {
		trigger: "new" | "duplicate";
		workspace: { uuid: string; name: string; taxonomy: string[] };
		source: { uuid: string; name: string };
	};
	"workspace.entityActions.delete": { workspace: { uuid: string; name: string } };
	"workspace.folderActions.create": { folder: { name: string } };
	"workspace.folderActions.delete": { folder: { name: string; taxonomy: string[] } };
	// The content of a workspace was reshaped (column/chart/filter added, edited or removed, view
	// switched, sorting or page size changed). `trigger` discriminates the interaction; the rest is a
	// snapshot of the workspace right after the change, i.e. what gets persisted upstream.
	"workspace.content.change": {
		trigger:
			| "setView"
			| "setSort"
			| "setPageSize"
			| "addColumn"
			| "addCustomPropertyColumn"
			| "editColumn"
			| "removeColumn"
			// reorder or show/hide of the existing columns
			| "setColumnsLayout"
			| "upsertFilter"
			| "addFilter"
			| "editFilter"
			| "removeFilter"
			| "removeAllFilters"
			| "addChart"
			| "editChart"
			| "removeChart"
			// reorder or show/hide of the existing charts
			| "setChartsLayout"
			| "addConcept"
			| "editConcept"
			| "pruneUnusedConcepts";
		// A workspace can be displayed before it exists upstream (the default one), hence the optionals.
		workspace: { uuid?: string; name?: string };
		view: ViewModeEnum;
		columns: number;
		visibleColumns: number;
		charts: number;
		visibleCharts: number;
		filters: number;
		privateConcepts: number;
		sharedConcepts: number;
		pageSize: number;
		sort: { parameterizedConceptUuid?: string; direction?: SortDirectionEnum } | null;
		// The configured columns, in order, identified for diagnostics: `uuid` is the parameterized
		// concept the column renders, `conceptId` the concept definition behind it.
		columnConcepts: { uuid: string; conceptId: string; hidden: boolean }[];
		// The configured charts, in order: type, number of plotted series, visibility.
		chartSeries: { chartType: string; series: number; hidden: boolean }[];
	};

	"portfolio.bulkActions.delete": { selectedPortfolios: number };
	"portfolio.bulkActions.downloadReport": { selectedPortfolios: number };
	"portfolio.bulkActions.downloadComposition": { selectedPortfolios: number };
	"portfolio.bulkActions.exportComposition": { selectedPortfolios: number };
	"portfolio.bulkActions.compare": { selectedPortfolios: number };
	"portfolio.bulkActions.createProposal": { intentId?: string; selectedPortfolios: number };
	"portfolio.bulkActions.acceptProposal": { selectedPortfolios: number; inThePast?: boolean };
	"portfolio.bulkActions.sendToExecution": { selectedPortfolios: number };
	"portfolio.bulkActions.stopExecution": { selectedPortfolios: number };
	"portfolio.bulkActions.rejectProposal": { selectedPortfolios: number };
	"portfolio.bulkActions.editSettings": { intentId: string; selectedPortfolios: number };
	"portfolio.bulkActions.generateCommentary": { selectedPortfolios: number };
	"portfolio.bulkActions.merge": { selectedPortfolios: number };
	"portfolio.bulkActions.share": { selectedPortfolios: number };
	"portfolio.bulkActions.createProposalIntent": { intentId: string; selectedPortfolios: number };
	"portfolio.entityAction.downloadReport": {
		portfolio: { uuid: string; name: string };
		template: { uuid: string; name: string };
	};
	"portfolio.entityAction.downloadProposalComposition": {
		portfolio: { uuid: string; name: string };
		format: InvestmentExportConverterType;
	};
	"portfolio.entityAction.downloadComposition": {
		portfolio: { uuid: string; name: string };
		format: InvestmentExportConverterType;
	};
	"portfolio.entityAction.downloadProposalReport": {
		portfolio: { uuid: string; name: string };
		template: { uuid: string; name: string };
	};
	"portfolio.entityAction.downloadProposalTrades": { portfolio: { uuid: string; name: string } };
	"portfolio.entityAction.downloadCommentary": { portfolio: { uuid: string; name: string } };
	"portfolio.entityAction.downloadProposalCommentary": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.execute": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.cancelExecution": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.regenerateCommentary": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.generateCommentary": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.generateProposalCommentary": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createPortfolioIntent": { intentId: string };
	"portfolio.entityActions.createProposalIntent": { intentId: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createAsDraftManual": { intentId?: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createAsPortfolioManual": { intentId?: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createAsDraftSphere": { intentId?: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createAsPortfolioSphere": { intentId?: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.createProposal": { intentId?: string; portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.acceptProposal": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.rejectProposal": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.editSettings": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.editProposalComposition": { portfolio: { uuid: string; name: string } };
	"portfolio.entityActions.editPortfolioComposition": { portfolio: { uuid: string; name: string } };
	"portfolio.import": { importedAsPortfolios: number; importedAsDrafts: number; failed: string[]; format: string };
	"reference.import": { imported: number; failed: string[]; format: string };
	"universe.import": { imported: number; failed: string[]; format: string };
	"marketView.import": { imported: number; failed: string[]; format: string };
	"portfolio.bulkAnalysis.sidePanel.open": null;
	"portfolio.bulkAnalysis.sidePanel.filters.search": null;
	"portfolio.bulkAnalysis.sidePanel.filters.change": null;
	"portfolio.bulkAnalysis.sidePanel.keyMetric.change": {
		metric: { key: FieldKey; metricParameters?: MetricParameters };
	};
	"portfolio.bulkAnalysis.sidePanel.keyMetric.reference": null;
	"portfolio.bulkAnalysis.sidePanel.keyMetric.threshold": null;
	"portfolio.bulkAnalysis.selectAll": { selectedPortfolios: number };
	"portfolio.bulkAnalysis.unselectAll": null;
	"portfolio.bulkAnalysis.selectPage": { selectedPortfolios: number };
	"portfolio.bulkAnalysis.unselectPage": null;
	"notificationSettings.alertsUpdated": EnableAlertDto[];
	"intercom.showHelp": null;
	"intercom.showArticle": [articleName: string, articleId: number];

	// A sign-in died on the OIDC callback. `reason` is the failure class the user was shown
	// (see `classifyAuthCallbackError`) and `status` the HTTP status when the failure came from
	// the identity fetch — together they tell whether a report is a protocol defect or an
	// unprovisioned account, which the screen alone cannot prove.
	"auth.callbackFailed": { reason: string; status?: number };
};

export function trackMixPanelEvent<K extends keyof MixPanelEventMap>(event: K, payload: MixPanelEventMap[K]): void {
	if (typeof window.mixpanel === "undefined") {
		return;
	}
	if (
		mixPanelConfig.impersonating ||
		window.location.hostname === "localhost" ||
		(mixPanelConfig.isInternal && mixPanelConfig.uid !== platformUser)
	) {
		return;
	}
	const defaults = {
		Domain: window.location.host,
		Page: window.location.pathname,
		Action: event,
		$distinct_id: mixPanelConfig.uid,
		Impersonate: mixPanelConfig.impersonating,
	};
	const mixpanelObj = {
		...defaults,
		...payload,
	};
	window.mixpanel.track(event, mixpanelObj);
}

export default initMixPanel;
